import { useMemo } from 'react';
import { Box } from '@mui/material';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  RisksType,
  RISK_STATUS,
  RISK_TYPE,
  ZONE_KEYS,
  OUTCOME_STATUS,
  Milestone,
  HEALTHCHECK_NAMES,
  useProjectSecurityContext,
  useNewProjectUrl,
} from 'domain/shared/newProject';
import RiskSection from '../../commercial/sections/RiskSection';
import { EMPTY_MESSAGES, SECTION_TITLES } from '../../commercial/constants';
import OutdatedMilestonesSection from './OutdatedMilestonesSection';
import { useDateService } from 'framework/utils';
import moment from 'moment';
import RequiredActionsSection, { RequiredActionItem } from '../../commercial/sections/RequiredActionsSection';
import { getHealthcheckByName } from 'domain/projects/newProject/shared/healthcheck';
import StakeholdersActionButton from '../StakeholdersActionButton';
import { ControlData } from '../../types';
import { getOutdatedRisks } from '../../utils';

type PMRisksProps = {
  data: ControlData;
  refetchControlsData?: () => Promise<unknown>;
};

const ProjectManagementSections = ({ data, refetchControlsData }: PMRisksProps) => {
  const { getToday } = useDateService();
  const { getStakeholdersUrl } = useNewProjectUrl();
  const { isAccessGranted } = useProjectSecurityContext();
  const healthChecks = data?.health?.healthChecks || [];

  const allRisks = data?.risks || [];

  const activeRisks = allRisks.filter(
    (risk: RisksType) => risk.type === RISK_TYPE.PROJECT_MANAGEMENT && risk.status === RISK_STATUS.ACTIVE,
  );

  const escalatedRisks = activeRisks.filter((risk: RisksType) => risk.isEscalated);
  const otherRisks = activeRisks.filter((risk: RisksType) => !risk.isEscalated);
  const outdatedRisks = getOutdatedRisks(activeRisks);

  const allMilestones = data?.milestones || [];
  const outdatedMilestones = allMilestones.filter(
    (milestone: Milestone) =>
      milestone.status === OUTCOME_STATUS.ACTIVE && moment(milestone.initialPlannedDate).isBefore(getToday(), 'date'),
  );

  const stakeholdersHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.STAKEHOLDERS);
  const canManageStakeholders = isAccessGranted(ACCESS_INFO_SECTIONS.STAKEHOLDER, ACCESS_INFO_ACTIONS.MANAGE);
  const stakeholderMessage = stakeholdersHealthcheck?.message?.toLowerCase() || '';

  const requiredActions = useMemo<RequiredActionItem[]>(() => {
    if (!canManageStakeholders || !stakeholdersHealthcheck) {
      return [];
    }

    const isNotDefined = stakeholderMessage.includes('not defined') || stakeholderMessage.includes('not set');

    return [
      {
        id: isNotDefined ? 'stakeholders-not-defined' : 'stakeholders-review',
        message: isNotDefined
          ? 'Stakeholder list is not defined.'
          : 'Stakeholder list has not been confirmed more than 1 month.',
        actions: (
          <StakeholdersActionButton
            isNotDefined={isNotDefined}
            onConfirmed={refetchControlsData}
            projectId={data.id}
            stakeholdersUrl={getStakeholdersUrl(data.id)}
          />
        ),
      },
    ];
  }, [
    canManageStakeholders,
    data.id,
    getStakeholdersUrl,
    refetchControlsData,
    stakeholderMessage,
    stakeholdersHealthcheck,
  ]);

  return (
    <Box sx={{ px: 2, py: 1 }}>
      {canManageStakeholders && stakeholdersHealthcheck?.targetZone !== ZONE_KEYS.SAFE && (
        <RequiredActionsSection title={SECTION_TITLES.REQUIRED_ACTIONS} actions={requiredActions} />
      )}
      <RiskSection
        title={SECTION_TITLES.ESCALATED_PROJECT_MANAGEMENT_RISKS}
        risks={escalatedRisks}
        emptyMessage={EMPTY_MESSAGES.ESCALATED_RISKS}
      />
      <RiskSection
        title={SECTION_TITLES.OTHER_PROJECT_MANAGEMENT_RISKS}
        risks={otherRisks}
        emptyMessage={EMPTY_MESSAGES.OTHER_RISKS}
      />
      {outdatedRisks.length > 0 && (
        <RiskSection
          title={SECTION_TITLES.OUTDATED_RISKS}
          risks={outdatedRisks}
          emptyMessage={EMPTY_MESSAGES.OTHER_RISKS}
        />
      )}
      <OutdatedMilestonesSection
        title={SECTION_TITLES.OUTDATED_MILESTONES}
        milestones={outdatedMilestones}
        emptyMessage={EMPTY_MESSAGES.OUTDATED_MILESTONES}
      />
    </Box>
  );
};

export default ProjectManagementSections;
